import React, { useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { formatDateTime } from '../../utils/dateFormat';
import {
  detailHeaderSticky, detailHeaderIdle, detailHeaderStuck,
  detailHeaderTitle, detailHeaderTitleIdle, detailHeaderTitleStuck,
} from './constants';

// The one workflow detail page frame (docs/workflow-ui.md §4.3): back link,
// error/notice banners and loading state that never lose navigation, max-w-4xl.
// Section order inside is fixed: <RequestHeader> → <StatusActionsCard> →
// content SectionCards → attachments → <HistoryCard>.
export const RequestDetailShell = ({
  backTo,
  backLabel,
  loading = false,
  error = null,
  notice = null,
  children,
}) => (
  <div className="p-4 sm:p-6 max-w-4xl mx-auto">
    <Link to={backTo} className="text-sm text-slate-500 hover:text-slate-700">← {backLabel}</Link>

    {error && (
      <div className="mt-4 bg-red-50 border border-red-200 rounded-lg p-4 text-red-700 text-sm">
        {error}
      </div>
    )}
    {notice && (
      <div className="mt-4 bg-green-50 border border-green-200 rounded-lg p-4 text-green-700 text-sm">
        {notice}
      </div>
    )}

    {loading
      ? !error && <p className="mt-8 text-center text-sm text-slate-500">Loading…</p>
      : children}
  </div>
);

// Detail page header: request no + type + (optional) status badge on the title
// row, then "submitted by … on <date time>" (house rule: detail shows minutes).
// `chips` renders extra pills next to the title (e.g. FSA doc-type).
export const RequestHeader = ({
  requestNo,
  typeLabel,
  badge = null,
  chips = null,
  submittedBy,
  submittedAt,
  // 記錄頁（Management）不是申請單:它沒有「誰送的」，只有「怎麼來的」。
  // 換掉整句而不是硬塞進 submittedBy，否則會讀成「submitted by Produced by
  // approval」。
  originLabel = null,
}) => {
  // 標頭黏在視窗頂端，所以捲到表單／歷程的中段也還知道自己在看哪一筆。黏住與
  // 否靠標頭正上方那條 1px 的哨兵：它捲出視窗的那一刻，標頭正好貼到頂。用
  // scroll 事件就得自己算位置（上方還有返回列、notice、NotLiveBanner，高度不
  // 固定），IntersectionObserver 不必算。
  const sentinelRef = useRef(null);
  const [stuck, setStuck] = useState(false);
  useEffect(() => {
    const el = sentinelRef.current;
    if (!el || typeof IntersectionObserver === 'undefined') return undefined;
    const obs = new IntersectionObserver(([entry]) => setStuck(!entry.isIntersecting));
    obs.observe(el);
    return () => obs.disconnect();
  }, []);

  return (
    <>
      <div ref={sentinelRef} aria-hidden="true" className="h-px" />
      <div className={`${detailHeaderSticky} ${stuck ? detailHeaderStuck : detailHeaderIdle}`}>
        <div className="flex flex-wrap items-center gap-3">
          <h1 className={`${detailHeaderTitle} ${stuck ? detailHeaderTitleStuck : detailHeaderTitleIdle}`}>{requestNo}</h1>
          {badge}
          {chips}
        </div>
        {/* 沒有日期就不要印孤零零的「on」——記錄／engagement 不一定帶時間戳。 */}
        {/* 客戶頁這類主檔頁 typeLabel 與來源都不傳，整行不印。標題已經講明類別的
            頁（QC6／QC7）只傳來源，來源就成了句首，靠 first-letter 撐大小寫。 */}
        {/* 黏住之後只留標題那一行：這行是「哪一筆」，副標是送件資訊，捲動中
            不需要，留著只會吃掉手機的半個螢幕。 */}
        {!stuck && (typeLabel || originLabel || submittedBy) && (
          <p className={`text-sm text-slate-500 mt-1${typeLabel ? '' : ' first-letter:uppercase'}`}>
            {typeLabel && <>{typeLabel} · </>}
            {originLabel || `submitted by ${submittedBy}`}
            {submittedAt ? ` on ${formatDateTime(submittedAt)}` : ''}
          </p>
        )}
      </div>
    </>
  );
};

export default RequestDetailShell;
